import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChevronDown, Star, Zap, Target, Brain, BarChart2, CheckCircle2 } from 'lucide-react';
import { SiWhatsapp, SiYoutube } from 'react-icons/si';
import { IMAGES } from '@/assets/images';
import { METRICS, CPAV_STEPS, SERVICES, BLOG_POSTS, formatDate, getCategoryColor, ROUTE_PATHS, SOCIAL_LINKS } from '@/lib/index';
import { springPresets } from '@/lib/motion';

// ─── Animation Helpers ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: springPresets.gentle },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

function Section({ children, id, className = '' }: { children: React.ReactNode; id?: string; className?: string }) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={`py-20 md:py-28 ${className}`}
    >
      {children}
    </motion.section>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.HERO_BG_9}
          alt="Background"
          className="w-full h-full object-cover opacity-10"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-3xl opacity-15 z-0"
        style={{ background: 'radial-gradient(ellipse, var(--primary), transparent 70%)' }}
      />

      <div className="container mx-auto px-4 relative z-10 text-center pt-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ ...springPresets.bouncy, delay: 0.1 }}
          className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary rounded-full px-4 py-1.5 text-xs font-semibold mb-6 font-mono"
        >
          <Zap className="w-3 h-3" />
          NEXUS MAXIMUS — SISTEMA DE DOMINAÇÃO POR DADOS
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.2 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight mb-6"
        >
          <span className="text-foreground">Onde a Engenharia</span>
          <br />
          <span className="text-foreground">de Dados Encontra</span>
          <br />
          <span
            className="relative"
            style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            o Lucro Exponencial
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.35 }}
          className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          A era do "testar criativo" acabou. O{' '}
          <span className="text-primary font-semibold">Protocolo Kamillo 2026</span> une
          Server-Side Tagging, Modelagem Bayesiana e IA Causal para entregar{' '}
          <span className="text-accent font-semibold">ROI Preditivo com até 95% de precisão</span>.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
        >
          <a
            href={SOCIAL_LINKS.WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl text-sm font-bold hover:opacity-90 active:scale-95 transition-all shadow-lg"
            style={{ boxShadow: '0 8px 24px color-mix(in srgb, var(--primary) 35%, transparent)' }}
          >
            <SiWhatsapp className="w-4 h-4" />
            Agendar Consultoria Gratuita
            <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#sobre"
            onClick={(e) => { e.preventDefault(); document.getElementById('sobre')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="flex items-center gap-2 border border-border text-muted-foreground px-7 py-3.5 rounded-xl text-sm font-medium hover:border-primary hover:text-primary transition-colors"
          >
            Conhecer o Sistema
          </a>
        </motion.div>

        {/* Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.65 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
        >
          {METRICS.map((m) => (
            <div
              key={m.label}
              className="bg-card border border-border rounded-xl p-4 text-center"
            >
              <div className="text-2xl md:text-3xl font-bold text-primary font-mono">{m.value}</div>
              <div className="text-xs font-semibold text-foreground mt-1">{m.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{m.description}</div>
            </div>
          ))}
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── SOBRE ────────────────────────────────────────────────────────────────────
function Sobre() {
  return (
    <Section id="sobre" className="bg-card/30">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image side */}
          <motion.div variants={fadeUp} className="relative">
            <div className="relative rounded-2xl overflow-hidden border border-border aspect-[4/3]">
              <img
                src={IMAGES.AI_BG_1}
                alt="Kamillo Santos - IA e Engenharia de Dados"
                className="w-full h-full object-cover opacity-80"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              {/* Floating badge */}
              <div className="absolute bottom-4 left-4 bg-card border border-border rounded-xl px-4 py-3 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs font-mono text-primary font-semibold">PROTOCOLO KAMILLO 2026</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Precisão preditiva: 95%</div>
              </div>
            </div>
            {/* Decorative card */}
            <div className="absolute -top-4 -right-4 bg-accent/10 border border-accent/30 rounded-xl p-3 hidden lg:block">
              <Brain className="w-6 h-6 text-accent" />
            </div>
          </motion.div>

          {/* Text side */}
          <div>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 text-accent rounded-full px-3 py-1 text-xs font-semibold mb-4">
              <Star className="w-3 h-3" />
              A MENTE POR TRÁS DA PERFORMANCE DE ELITE
            </motion.div>

            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Não sou apenas um gestor de tráfego.{' '}
              <span className="text-primary">Sou um Engenheiro de Tráfego.</span>
            </motion.h2>

            <motion.div variants={fadeUp} className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Com certificações de elite do Google e uma trajetória marcada pela gestão de
                orçamentos multimilionários, desenvolvi o{' '}
                <span className="text-foreground font-semibold">Protocolo Kamillo 2026</span> — um
                sistema proprietário que une Server-side Tagging, Modelagem Bayesiana e Inteligência
                Artificial Causal.
              </p>
              <p>
                Minha missão é transformar orçamentos de publicidade em{' '}
                <span className="text-foreground font-semibold">
                  sistemas previsíveis de geração de receita em escala global
                </span>
                .
              </p>
              <p>
                Em Salvador, aplico o{' '}
                <span className="text-primary font-semibold">Framework KJS</span> para dominar o
                tráfego local — de pizzarias a concessionárias, cada nicho tem seus padrões de dados
                que dominamos com precisão cirúrgica.
              </p>
            </motion.div>

            {/* Quote */}
            <motion.blockquote
              variants={fadeUp}
              className="mt-6 border-l-2 border-primary pl-4 italic text-muted-foreground text-sm"
            >
              "O marketing digital que você conheceu em 2024 morreu. Em 2026, a escala não vem do
              esforço, vem da IA PREDITIVA."
            </motion.blockquote>

            {/* Tags */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mt-6">
              {['Google Certified', 'Server-Side Tagging', 'Bayesian ML', 'Neural Bidding', 'RL Algorithms'].map((tag) => (
                <span key={tag} className="px-3 py-1 bg-muted rounded-full text-xs text-muted-foreground border border-border">
                  {tag}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── METODOLOGIA C.P.A.V. ─────────────────────────────────────────────────────
function Metodologia() {
  return (
    <Section id="metodologia">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <Target className="w-3 h-3" />
            METODOLOGIA PROPRIETÁRIA
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-4">
            Metodologia{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              C.P.A.V.
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
            A ciência por trás do ROI 100x. Cada etapa foi desenvolvida para eliminar desperdício e
            maximizar a precisão na tomada de decisão.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CPAV_STEPS.map((step, i) => (
            <motion.div
              key={step.letter}
              variants={fadeUp}
              className="relative group bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-all duration-300"
              style={{ boxShadow: '0 4px 24px -8px transparent' }}
              whileHover={{ y: -4 }}
            >
              {/* Number */}
              <div className="absolute top-4 right-4 text-6xl font-bold text-primary/5 font-mono select-none">
                0{i + 1}
              </div>
              {/* Icon/Letter */}
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                <span className="text-2xl">{step.icon}</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-2xl font-bold text-primary font-mono">{step.letter}</span>
                <span className="text-lg font-semibold text-foreground">{step.title}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack Banner */}
        <motion.div
          variants={fadeUp}
          className="mt-16 bg-card border border-border rounded-2xl p-8"
        >
          <h3 className="text-center font-semibold mb-6 text-muted-foreground text-sm uppercase tracking-widest">
            Tecnologias do Protocolo Kamillo 2026
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '🔗', label: 'Server-Side Tagging', desc: 'Zero perda de dados' },
              { icon: '🧠', label: 'Modelagem Bayesiana', desc: 'Previsão 95% precisa' },
              { icon: '⚡', label: 'Neural Bidding', desc: 'Reinforcement Learning' },
              { icon: '📐', label: 'IA Causal', desc: 'Causa real do ROI' },
            ].map((tech) => (
              <div key={tech.label} className="flex items-start gap-3 p-4 bg-muted/30 rounded-xl border border-border">
                <span className="text-2xl">{tech.icon}</span>
                <div>
                  <div className="text-sm font-semibold text-foreground">{tech.label}</div>
                  <div className="text-xs text-muted-foreground">{tech.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

// ─── SERVIÇOS ─────────────────────────────────────────────────────────────────
function Servicos() {
  return (
    <Section id="servicos" className="bg-card/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 text-accent rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <BarChart2 className="w-3 h-3" />
            FRAMEWORK KJS — SALVADOR/BA
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-4">
            Dominação de Tráfego{' '}
            <span className="text-accent">Local em Salvador</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground max-w-xl mx-auto">
            Cada nicho tem padrões de dados únicos. O Framework KJS explora esses padrões para
            colocar seu anúncio na frente de quem vai comprar agora.
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((svc) => (
            <motion.div
              key={svc.title}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="bg-card border border-border rounded-2xl p-6 hover:border-accent/40 transition-all duration-300"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center text-2xl flex-shrink-0">
                  {svc.icon}
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">{svc.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{svc.description}</p>
                </div>
              </div>
              <ul className="space-y-2 mt-4">
                {svc.features.map((feat) => (
                  <li key={feat} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ─── BLOG PREVIEW ─────────────────────────────────────────────────────────────
function BlogPreview() {
  const recent = BLOG_POSTS.slice(0, 3);

  return (
    <Section id="blog">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-4">
              <Brain className="w-3 h-3" />
              BLOG — CIÊNCIA APLICADA
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold">
              Conhecimento que{' '}
              <span className="text-primary">Gera Resultado</span>
            </motion.h2>
          </div>
          <motion.div variants={fadeUp}>
            <Link
              to={ROUTE_PATHS.BLOG}
              className="flex items-center gap-2 text-primary text-sm font-semibold hover:underline"
            >
              Ver todos os artigos <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {recent.map((post) => (
            <motion.article
              key={post.slug}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/40 transition-all duration-300 flex flex-col"
            >
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getCategoryColor(post.category)}`}>
                    {post.category}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{post.readTime}</span>
                </div>
                <h3 className="font-bold text-base leading-snug mb-3 text-foreground line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">{formatDate(post.date)}</span>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
                  >
                    Ler <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ─── CONTATO ──────────────────────────────────────────────────────────────────
function Contato() {
  return (
    <Section id="contato" className="bg-card/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-6">
            <Zap className="w-3 h-3" />
            CONECTE-SE À ELITE
          </motion.div>

          <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            Pronto para elevar seu negócio ao nível da{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              IA Preditiva?
            </span>
          </motion.h2>

          <motion.p variants={fadeUp} className="text-muted-foreground text-lg mb-10 leading-relaxed">
            Agende sua consultoria gratuita e descubra como o Protocolo Kamillo 2026 pode
            transformar seu orçamento de mídia em um sistema previsível de receita.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a
              href={SOCIAL_LINKS.WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-xl text-base font-bold hover:opacity-90 active:scale-95 transition-all w-full sm:w-auto justify-center"
              style={{ boxShadow: '0 8px 32px color-mix(in srgb, var(--primary) 40%, transparent)' }}
            >
              <SiWhatsapp className="w-5 h-5" />
              Agendar no WhatsApp
            </a>
            <a
              href={SOCIAL_LINKS.YOUTUBE}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-border text-muted-foreground px-8 py-4 rounded-xl text-base font-medium hover:border-primary hover:text-primary transition-all w-full sm:w-auto justify-center"
            >
              <SiYoutube className="w-5 h-5" />
              Canal no YouTube
            </a>
          </motion.div>

          {/* Trust Signals */}
          <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: '🔒', label: 'Consultoria 100% gratuita', desc: 'Sem compromisso' },
              { icon: '📊', label: 'Análise personalizada', desc: 'Do seu negócio' },
              { icon: '🚀', label: 'Resultados em 30 dias', desc: 'Ou reformulamos' },
            ].map((item) => (
              <div key={item.label} className="bg-card border border-border rounded-xl p-4 text-center">
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-sm font-semibold text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ─── HOME PAGE ────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <>
      <Hero />
      <Sobre />
      <Metodologia />
      <Servicos />
      <BlogPreview />
      <Contato />
    </>
  );
}
